import styles from "./wizard.module.scss";
import { Alert, Button, Paper, Typography } from "@mui/material";
import { ThemeProvider } from '@mui/material/styles';
import { useForm } from "react-hook-form";
import { useCustomTheme } from "services/hooks";
import { countryOptions } from "services/geo/countries";
import SelectInput from "components/forms/SelectInput";
import RadioGroup from "components/forms/RadioGroup";
import { useEffect } from "react";
import TextInput from "components/forms/TextInput";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { isEmpty } from "lodash";
import { handleToggleCheckout, resetWizard } from "store/wizard";
import { ForwardNavLink } from "./components/Menu";

export const heatingTechnologyOptions_1 = [
  { name: "Biogas (kWh)", code: 0 },
  { name: "Biogas (m3)", code: 1 },
  { name: "District Heating (kWh)", code: 2 },
  { name: "Heat pump thermal energy (kWh)", code: 3 },
  { name: "Heat pump air/water (kWh)", code: 4 },
  { name: "Heating oil (kWh)", code: 5 },
  { name: "Heating oil (L)", code: 6 },
  { name: "Natual gas (kWh)", code: 7 },
  { name: "Natual gas (m3)", code: 8 },
  { name: "Pellets (kWh)", code: 9 },
  { name: "Pellets (kg)", code: 10 },
  { name: "Solar thermal energy (kWh)", code: 11 },
  { name: "Wood chips (kWh)", code: 12 },
  { name: "Wood chips (kg)", code: 13 }
];

export const heatingTechnologyOptions_2 = [
  { name: "Biogas", code: 0 },
  { name: "District Heating", code: 1 },
  { name: "Heat pump thermal energy", code: 2 },
  { name: "Heat pump air/water", code: 3 },
  { name: "Heating oil", code: 4 },
  { name: "Natual gas", code: 5 },
  { name: "Pellets", code: 6 },
  { name: "Solar thermal energy", code: 7 },
  { name: "Wood chips", code: 8 },
];

const Household = (props:any) => {
  const customTheme = useCustomTheme();
  const { selection } = useAppSelector((state) => state.wizard);
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  
  } = useForm();

  const watchHeat = watch("heat");
  const watchPhotovoltaicSystem = watch("photovoltaicSystem");

  useEffect(() => {
    if(watchHeat) {
      setValue("heatingTechnology", "");
      setValue("consumption", "");
    }
  },[watchHeat,setValue]);

  useEffect(() => {
    if(watchPhotovoltaicSystem) {
      setValue("generated", "");
      setValue("fed", "");
    }
  },[watchPhotovoltaicSystem,setValue]);

  const onSubmit = (form) => {
    console.log(form)
    dispatch(handleToggleCheckout(true));
  }

  return (
    <>
      <ThemeProvider theme={customTheme}>
        <Paper className={`${styles.wizardWrapper}`} elevation={3}>
          <div className="row">
            <div className="col-lg-8 col-xl-8 col-12">
              {
                !isEmpty(selection) && (
                  <Alert severity="info" className="mb-3">
                    You currently have an active calculation for Individual Footprint.
                    <div className="d-flex mt-2">
                      <Button color="inherit" variant="outlined" size="small" className="me-2" onClick={() => dispatch(resetWizard())}>
                        Start Over
                      </Button>
                      <Button color="inherit" size="small" component={ForwardNavLink} to={"/individual"}>
                        Go Back
                      </Button>
                    </div>
                  </Alert>
                )
              }
              <form onSubmit={handleSubmit(onSubmit)}>
                <Typography variant="body2" textTransform="uppercase" color="#000" fontWeight={700} className="mb-3">
                  General Information
                </Typography>
                <SelectInput
                  control={control}
                  name="country" 
                  label="Country"
                  errors={errors}
                  rules={{
                    required: "This field is required",
                  }}
                  options={countryOptions}
                />
                <Typography variant="body2" textTransform="uppercase" color="#000" fontWeight={700} className="mb-0">
                  Heat
                </Typography>
                <div className="mb-3">
                  <RadioGroup 
                    control={control} 
                    name="heat" 
                    label={""} 
                    options={[
                      { label: "I know the consumption and would like to enter it directly.", value: 0 },
                      { label: "I don't know the exact amount and I would like to make a rough estimation based on the living area (m2) and the building standards.", value: 1 },
                    ]} 
                    defaultValue={"0"}
                  />
                </div>
                {
                  (!watchHeat || watchHeat === "0") ? (
                    <>
                      <SelectInput
                        control={control}
                        name="heatingTechnology" 
                        label="Heating Technology"
                        errors={errors}
                        rules={{
                          required: "This field is required",
                        }}
                        options={heatingTechnologyOptions_1}
                      />
                      <TextInput
                        control={control}
                        name="consumption" 
                        label="Consumption / Useful Heat"
                        errors={errors}
                        rules={{
                          required: "This field is required",
                          pattern: {
                            value: /^[0-9]*\.?[0-9]+$/,
                            message: "Enter a valid number",
                          },
                        }}
                      />
                    </>
                  ) : (
                    <SelectInput
                      control={control}
                      name="heatingTechnology" 
                      label="Heating Technology"
                      errors={errors}
                      rules={{
                        required: "This field is required",
                      }}
                      options={heatingTechnologyOptions_2}
                    />
                  )
                }
                <Typography variant="body2" textTransform="uppercase" color="#000" fontWeight={700} className="mb-3">
                  Electricity
                </Typography>
                <TextInput
                  control={control}
                  name="power" 
                  label="Power Consumption"
                  errors={errors}
                  rules={{
                    required: "This field is required",
                    pattern: {
                      value: /^[0-9]*\.?[0-9]+$/,
                      message: "Enter a valid number",
                    },
                  }}
                  adornment="kWh"
                />
                <div className="mb-3">
                  <RadioGroup 
                    control={control} 
                    name="greenEnergy" 
                    label="Green energy" 
                    options={[
                      { label: "Yes", value: 0 },
                      { label: "No", value: 1 },
                    ]} 
                    defaultValue={"1"}
                  />
                </div>
                <div className="mb-3">
                  <RadioGroup 
                    control={control} 
                    name="photovoltaicSystem" 
                    label="Photovoltaic System" 
                    options={[
                      { label: "Yes", value: 0 },
                      { label: "No", value: 1 },
                    ]} 
                    defaultValue={"1"}
                  />
                </div>
                {
                  watchPhotovoltaicSystem === "0" && (
                    <>
                      <TextInput
                        control={control}
                        name="generated" 
                        label="Electricity Generated (total)"
                        errors={errors}
                        rules={{
                          required: "This field is required",
                          pattern: {
                            value: /^[0-9]*\.?[0-9]+$/,
                            message: "Enter a valid number",
                          },
                        }}
                        adornment="kWh"
                      />
                      <TextInput
                        control={control}
                        name="fed" 
                        label="Electricity fed into the grid"
                        errors={errors}
                        rules={{
                          required: "This field is required",
                          pattern: {
                            value: /^[0-9]*\.?[0-9]+$/,
                            message: "Enter a valid number",
                          },
                        }}
                        adornment="kWh"
                      />
                    </>
                  )
                }
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  type="submit"
                  disabled={!isEmpty(selection)}
                >
                  Calculate
                </Button>
              </form>
            </div>
          </div>
        </Paper>
      </ThemeProvider>
    </>
  );
};
export default Household;
